import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';

import FormAuthEnforcement, { FormAuthEnforcementData } from 'dashboard/forms/FormAuthEnforcement';
import { StartEnforcementValue } from 'dashboard/forms/FormAuthEnforcement/FormAuthEnforcement';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { Iso8601DateString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

interface SamlRequirementModalProps {
  handleClose: () => void;
  handleSubmit: (values: Iso8601DateString) => Promise<void>;
}

export default ({ handleClose, handleSubmit }: SamlRequirementModalProps) => {
  const initialValues = useMemo(
    () => ({
      startEnforcement: StartEnforcementValue.Immediately,
      fromDate: DateTime.local().plus({ month: 1 }).toISO(),
    }),
    [],
  );

  const handleEnableSamlRequirement = useCallback(
    async ({ startEnforcement, fromDate }: FormAuthEnforcementData) => {
      switch (startEnforcement) {
        case StartEnforcementValue.Immediately:
          await handleSubmit(new Date().toISOString());
          handleClose();
          break;
        case StartEnforcementValue.FromDate:
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          await handleSubmit(DateTime.fromISO(fromDate).toISO());
          handleClose();
          break;
        default:
          assertNever(startEnforcement);
      }
    },
    [],
  );

  return (
    <ModalContainer data-test='saml-requirement-modal'>
      <ModalHeader data-test='saml-requirement-modal__header' onClose={handleClose}>
        Enforce SAML authentication for your whole organization
      </ModalHeader>

      <ModalBody>
        <>
          <Paragraph>
            Users without SAML authentication configured will be locked out of their accounts.
          </Paragraph>
          <Spacer />
          <FormAuthEnforcement
            initialValues={initialValues}
            onSubmit={handleEnableSamlRequirement}
          />
        </>
      </ModalBody>
    </ModalContainer>
  );
};
